/** The command palette: jump to any screen, Help section or the register by typing its name.
 *
 * **Opened by Cmd+K or Ctrl+K from anywhere in the shell** (`AppShell` owns the listener, so the
 * shortcut works while focus is in a table cell as well as on the page), or by the search field in the top
 * bar. Built on the stack that is already here: no dialog or combobox library, because a palette is
 * one input, one list and four keys, and rule 1 makes a new dependency an ADR.
 *
 * **Keyboard, which is the whole point of it.** ArrowDown and ArrowUp move (wrapping), Enter opens
 * the highlighted entry, Escape closes. Focus is **trapped** - Tab cannot leave the dialog, and a
 * focus that is moved out by anything else is pulled back - and **returned** to whatever had it
 * when the palette opened, which is design rule 10 (a control that takes focus gives it back).
 *
 * **It navigates exactly as the rail does.** The Scope 2 method and the reporting window are
 * carried across, so jumping to a screen cannot silently change which inventory or which year is
 * being read. A screen opened from here is a fresh screen: a `tab` or `q` left over from the one
 * being left would filter something nobody asked for.
 *
 * **It reads nothing from the API.** Its sources are the rail's own list and Help's static text
 * (`paletteModel.ts`), so it has no loading state to get stuck in and no failure to mislabel as
 * "no results". The last entry for any query is the register search, so it is never empty.
 *
 * **Motion confirms the state change and nothing else.** The scrim fades and the dialog settles
 * 8px down into place over 140ms, on opacity and transform only, and leaves the same way
 * (`AppShell` wraps it in `AnimatePresence`). Under reduced motion it appears and disappears at its
 * final state with no transition at all.
 *
 * ARIA is the combobox-with-listbox pattern with `aria-activedescendant`: DOM focus stays in the
 * input, which is what lets typing and arrowing happen together, and the highlighted option is
 * named by the input. The highlight is a tint, a left bar and a return arrow - never colour alone.
 */

import { ArrowElbowDownLeft, BookOpen, MagnifyingGlass } from "@phosphor-icons/react";
import { motion, useReducedMotion } from "motion/react";
import type { RefObject } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { useNavigate } from "@tanstack/react-router";

import type { Scope2Method } from "../api/queries";
import type { Window } from "../screens/useScreen";
import styles from "./CommandPalette.module.css";
import { helpIndex } from "./helpContent";
import type { NavItem } from "./navItems";
import type { PaletteEntry } from "./paletteModel";
import { flatten, paletteSections } from "./paletteModel";

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function CommandPalette({
  screens,
  method,
  period,
  onClose,
  fallbackFocus,
}: {
  screens: readonly NavItem[];
  method: Scope2Method;
  period: Window | undefined;
  onClose: () => void;
  /** Where focus goes if whatever had it when the palette opened is no longer in the page. */
  fallbackFocus?: RefObject<HTMLElement | null>;
}) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const fade = { duration: reduce ? 0 : 0.14, ease: "easeOut" as const };
  const navigate = useNavigate();
  const id = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const sections = useMemo(() => paletteSections(query, screens, helpIndex()), [query, screens]);
  const entries = useMemo(() => flatten(sections), [sections]);
  // Derived rather than reset in an effect: a query that shortens the list can leave `active` past
  // the end for one render, and an out-of-range index must not name an option that is not there.
  const at = entries.length === 0 ? -1 : Math.min(active, entries.length - 1);
  // Where each section starts in the flat list, so an option knows its own position without a
  // counter that is reassigned while rendering.
  const starts = sections.reduce<number[]>((acc, _section, at) => {
    acc.push(at === 0 ? 0 : (acc[at - 1] ?? 0) + (sections[at - 1]?.entries.length ?? 0));
    return acc;
  }, []);
  const optionId = (entry: PaletteEntry) => `${id}-${entry.id}`;

  // Focus in on open, and back out on close. The opener is read once, when the palette mounts: it
  // is whatever the reader was on, which is the thing they expect to be on again.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // The fallback is the top bar's own button, which outlives the palette, so reading it now is
    // reading it then.
    const fallback = fallbackFocus?.current ?? null;
    input.current?.focus();
    return () => {
      (opener?.isConnected ? opener : fallback)?.focus();
    };
  }, [fallbackFocus]);

  // The trap's second half. Tab is handled below, but a click on the page behind the scrim, a
  // script or a screen reader's virtual cursor can still move focus out; take it back.
  useEffect(() => {
    const pull = (event: FocusEvent) => {
      if (dialog.current && event.target instanceof Node && !dialog.current.contains(event.target)) {
        input.current?.focus();
      }
    };
    document.addEventListener("focusin", pull);
    return () => document.removeEventListener("focusin", pull);
  }, []);

  // Keep the highlighted option on screen as the arrows move it. `scrollIntoView` is absent in
  // jsdom, hence the optional call.
  const activeId = at >= 0 && entries[at] ? optionId(entries[at]) : undefined;
  useEffect(() => {
    if (activeId) document.getElementById(activeId)?.scrollIntoView?.({ block: "nearest" });
  }, [activeId]);

  const choose = (entry: PaletteEntry) => {
    // The method and the window travel with the reader, exactly as they do on the rail.
    const carry = (prev: Record<string, unknown>, extra: Record<string, unknown> = {}) => ({
      ...prev,
      method,
      from: period?.period_start,
      to: period?.period_end,
      ...extra,
    });
    onClose();
    void navigate({
      to: entry.to,
      search: (prev: Record<string, unknown>) =>
        entry.kind === "help"
          ? carry(prev, { tab: entry.tab })
          : entry.kind === "register"
            ? carry(prev, { q: entry.q })
            : carry(prev, { tab: undefined, q: undefined }),
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        onClose();
        break;
      case "ArrowDown":
        event.preventDefault();
        if (entries.length > 0) setActive((at + 1) % entries.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        if (entries.length > 0) setActive((at - 1 + entries.length) % entries.length);
        break;
      case "Enter": {
        // An Enter that confirms an IME composition is not a request to navigate.
        if (event.nativeEvent.isComposing) break;
        event.preventDefault();
        const entry = entries[at];
        if (entry) choose(entry);
        break;
      }
      case "Tab": {
        // The trap. With one focusable control this is a no-op that stays put; if a control is
        // ever added, Tab wraps within the dialog rather than escaping to the page behind it.
        event.preventDefault();
        const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
        if (controls.length === 0) break;
        const here = controls.indexOf(document.activeElement as HTMLElement);
        const next = event.shiftKey ? here - 1 : here + 1;
        controls[(next + controls.length) % controls.length]?.focus();
        break;
      }
      default:
    }
  };

  return (
    <motion.div
      className={styles.backdrop}
      initial={reduce ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={fade}
      // A press on the scrim, not on the dialog, closes it. `mousedown` so a drag that starts in the
      // input and ends outside (selecting text) does not.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.div
        ref={dialog}
        initial={reduce ? false : { opacity: 0, y: -8, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8, scale: 0.985 }}
        transition={fade}
        role="dialog"
        aria-modal="true"
        aria-label="Jump to"
        className={styles.dialog}
        data-testid="command-palette"
        onKeyDown={onKeyDown}
      >
        <div className={styles.field}>
          <MagnifyingGlass className={styles.fieldIcon} size={18} aria-hidden="true" />
          <input
            ref={input}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={`${id}-list`}
            aria-autocomplete="list"
            aria-activedescendant={activeId}
            aria-label="Jump to a screen, a Help section or a record"
            placeholder="Jump to a screen, a Help section or a record"
            autoComplete="off"
            spellCheck={false}
            className={styles.input}
            data-testid="palette-input"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
          />
          <kbd className={styles.esc}>Esc</kbd>
        </div>

        <div id={`${id}-list`} role="listbox" aria-label="Results" className={styles.list}>
          {sections.map((section, sectionAt) => (
            <div
              key={section.heading}
              role="group"
              aria-labelledby={`${id}-h${sectionAt}`}
              className={styles.section}
            >
              <div className={styles.heading} id={`${id}-h${sectionAt}`}>
                {section.heading}
              </div>
              {section.entries.map((entry, entryAt) => {
                const mine = (starts[sectionAt] ?? 0) + entryAt;
                const isActive = mine === at;
                const Glyph =
                  entry.nav?.icon ?? (entry.kind === "help" ? BookOpen : MagnifyingGlass);
                return (
                  <div
                    key={entry.id}
                    id={optionId(entry)}
                    role="option"
                    aria-selected={isActive}
                    className={styles.option}
                    data-active={isActive}
                    data-testid="palette-option"
                    data-kind={entry.kind}
                    data-to={entry.to}
                    // `mousemove`, not `mouseenter`: the list scrolls under a stationary pointer as
                    // the arrows move the highlight, and that must not hand the highlight back.
                    onMouseMove={() => {
                      if (!isActive) setActive(mine);
                    }}
                    onClick={() => choose(entry)}
                  >
                    <Glyph className={styles.optionIcon} size={16} aria-hidden="true" />
                    <span className={styles.optionLabel}>{entry.label}</span>
                    <span className={styles.optionDetail}>{entry.detail}</span>
                    <ArrowElbowDownLeft
                      className={styles.optionEnter}
                      size={14}
                      aria-hidden="true"
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className={styles.footer} aria-hidden="true">
          <span>
            <kbd className={styles.key}>{"↑"}</kbd>
            <kbd className={styles.key}>{"↓"}</kbd> move
          </span>
          <span>
            <kbd className={styles.key}>{"↵"}</kbd> open
          </span>
          <span>
            <kbd className={styles.key}>Esc</kbd> close
          </span>
        </div>

        {/* Exists from the first render, so a change in it is announced. */}
        {/* `data-testid`, because the browser gate's S20 section counts results and reads this
            sentence. It used to select `shell-search-status` on the rail's own search, which the
            prototype replaced with one search in the top bar; the gate is retargeted here rather
            than losing the coverage. */}
        <p
          className="visuallyHidden"
          role="status"
          aria-live="polite"
          data-testid="palette-status"
        >
          {query.trim() ? `${entries.length} ${entries.length === 1 ? "result" : "results"}` : ""}
        </p>
      </motion.div>
    </motion.div>
  );
}
